import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { RootStore } from 'store/types';
import {
  IStandardDocumentData,
  IStandardDocumentVersionData,
  sortedStandardDocuments,
} from 'types/order/documents/shared/interfaces';
import { eDocumentVisibilityType } from 'types/order/documents/eDocumentVisibilityType';
import { IUserData } from 'types/users/IUser';
import { EditDocumentModalController } from './EditDocumentModalController';
import { DeleteDocumentModal } from './DeleteDocumentModal/DeleteDocumentModal';
import { StyledDocumentsTable } from './StyledDocumentsTable';
import { generateStandardDocumentsTableColumns } from './generateStandardDocumentsTableColumns';
import { createDownloadDocumentUrl } from 'API/order/documents/adminDocumentsAPI';
import { PDFPreview } from 'ui-toolkit/components/PDFPreview';
import { FinalizeDocumentModal } from './FinalizeDocumentModal';
import { DocumentDetailsSideSheetContainer } from './DocumentDetailsSideSheetContainer';
import {
  buildGetCategoryDescriptionByName,
  buildGetCategoryLabelByName,
  IDocumentCategory,
} from 'types/order/documents/IDocumentCategoryType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../API/documentCategories/DocumentCategoriesApi';
import { IParty } from '../../../../ui-toolkit/orders/interfaces/IParty';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';

export interface IStandardDocumentsTableProps {
  documents: IStandardDocumentVersionData[];
  documentsActionError: string | null;
  documentsActionFetchStatus: IFetchStatus;
  editDocumentFetch: IFetchStatus;
  isAmericanDigital: boolean;
  parties: IParty[];
  onDeleteDocumentReset: () => void;
  onEditDocument: (document: IStandardDocumentData) => Promise<any>;
  onEditDocumentReset: () => void;
  onFundingClick: (documentId: string, versionId: string, funding: boolean) => void;
  onPublish: (documentId: string, visibilityType: eDocumentVisibilityType) => void;
  onSigningClick: (documentId: string, versionId: string, signing: boolean) => void;
  onUnpublish: (documentId: string, visibilityType: eDocumentVisibilityType) => void;
  users: IUserData[];
  categories: IDocumentCategory[];
  onDetailsClick: (document: IStandardDocumentVersionData) => void;
  onCloseDetailsSideSheet: () => void;
  isZDocLoading: boolean;
}

interface IExtendedTableProps extends IStandardDocumentsTableProps {
  setShowGenerateLinkModal: (val: IStandardDocumentVersionData | null) => void;
  onFinalizeDocument: (docId: string, versionId: string) => void;
  resetFinalDocumentForm: () => void;
  onCloseFinalizeModal: () => void;
}

export const StandardDocumentsTable: React.FC<IExtendedTableProps> = ({
  documents,
  editDocumentFetch,
  isAmericanDigital,
  onDeleteDocumentReset,
  onEditDocument,
  onEditDocumentReset,
  onFundingClick,
  onPublish,
  onSigningClick,
  onUnpublish,
  setShowGenerateLinkModal,
  users,
  onFinalizeDocument,
  onCloseFinalizeModal,
  categories,
  onDetailsClick,
  onCloseDetailsSideSheet,
  isZDocLoading,
}) => {
  const [editDocument, setDocument] = useState<IStandardDocumentData | null>(null);
  const [previewDoc, setPreviewDoc] = useState<IStandardDocumentVersionData | null>(null);
  const [deleteDocument, setDeleteDocument] = useState<IStandardDocumentVersionData | null>(null);
  const [docToFinalize, setDocToFinalize] = useState<IStandardDocumentData | null>(null);

  const { documentDetails } = useSelector((state: RootStore) => state.order.documents.adminDocuments);
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const getCategoryLabel = buildGetCategoryLabelByName(documentCategoriesByName);
  const getCategoryDescription = buildGetCategoryDescriptionByName(documentCategoriesByName);

  const columns = useMemo(
    () =>
      generateStandardDocumentsTableColumns({
        onFundingClick,
        onPublish,
        onSigningClick,
        onUnpublish,
        setDeleteDocument,
        setPreviewDoc,
        setShowGenerateLinkModal,
        users,
        onFinalizeDocument,
        setDocToFinalize,
        getCategoryLabel,
        onDetailsClick,
        getCategoryDescription,
        isZillowClosingEnabled,
      }),
    [
      onFundingClick,
      onPublish,
      onSigningClick,
      onUnpublish,
      setDeleteDocument,
      setPreviewDoc,
      setShowGenerateLinkModal,
      users,
      onFinalizeDocument,
      setDocToFinalize,
      getCategoryLabel,
      onDetailsClick,
      getCategoryDescription,
      isZillowClosingEnabled,
    ]
  );

  const data = useMemo(() => {
    return sortedStandardDocuments(documents, categories).map((doc) => ({ ...doc, isZDocLoading }));
  }, [categories, documents, isZDocLoading]);

  return (
    <>
      {editDocument && (
        <EditDocumentModalController
          document={editDocument}
          hasSaveError={!editDocumentFetch.success}
          isComplete={editDocumentFetch.fetched}
          isLoading={editDocumentFetch.fetching}
          onClose={() => {
            setDocument(null);
            onEditDocumentReset();
          }}
          onEdit={onEditDocument}
          standardDocumentCategories={categories}
          documentCategoriesByName={documentCategoriesByName}
        />
      )}
      {previewDoc && (
        <PDFPreview
          file={createDownloadDocumentUrl(previewDoc.id, previewDoc.versionId)}
          onClose={() => setPreviewDoc(null)}
        />
      )}
      {deleteDocument && (
        <DeleteDocumentModal
          document={deleteDocument}
          onClose={() => {
            setDeleteDocument(null);
            onDeleteDocumentReset();
          }}
        />
      )}
      {documentDetails && (
        <>
          <DocumentDetailsSideSheetContainer
            isAmericanDigital={isAmericanDigital}
            document={documentDetails}
            onShowGoodThroughDetails={() => setDocument(documentDetails)}
            users={users}
            setPreviewDoc={setPreviewDoc}
            onClose={onCloseDetailsSideSheet}
          />
        </>
      )}
      {docToFinalize && (
        <FinalizeDocumentModal
          setDocToFinalize={setDocToFinalize}
          docToFinalize={docToFinalize}
          onCloseFinalizeModal={onCloseFinalizeModal}
        />
      )}

      <StyledDocumentsTable
        defaultSorted={[{ dataField: 'fileUpdatedAt', order: 'desc' }]}
        columns={columns}
        keyField={'id'}
        data={data}
      />
    </>
  );
};
